// Username validation - hanya huruf, angka, dan underscore
export function validateUsername(username: string): { valid: boolean; message?: string } {
    if (!username || !username.trim()) {
        return { valid: false, message: 'Username tidak boleh kosong' };
    }

    // Panjang minimal 6 karakter dan maksimal 20 karakter
    if (username.length < 6) {
        return { valid: false, message: 'Username minimal 6 karakter' };
    }

    if (username.length > 20) {
        return { valid: false, message: 'Username maksimal 20 karakter' };
    }

    // Hanya boleh huruf (a-z, A-Z), angka (0-9), dan underscore (_)
    if (!/^[A-Za-z0-9_]+$/.test(username)) {
        return { valid: false, message: 'Username hanya boleh mengandung huruf, angka, dan underscore (_)' };
    }

    return { valid: true };
}

// Email validation
export function validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

// Password strength checker
export interface PasswordStrength {
    score: number; // 0-4
    feedback: string[];
    isValid: boolean;
}

export function checkPasswordStrength(password: string): PasswordStrength {
    const feedback: string[] = [];
    let score = 0;

    // Length check
    if (password.length >= 8) {
        score++;
    } else {
        feedback.push('At least 8 characters');
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
        score++;
    } else {
        feedback.push('One uppercase letter');
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
        score++;
    } else {
        feedback.push('One lowercase letter');
    }

    // Number check
    if (/\d/.test(password)) {
        score++;
    } else {
        feedback.push('One number');
    }

    // Special character check (optional but adds to strength)
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        score++;
    }

    return {
        score: Math.min(score, 4),
        feedback,
        isValid: score >= 4,
    };
}

// Phone number validation (optional field)
export function validatePhone(phone: string): boolean {
    // Basic phone validation - accepts various formats
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    return !phone || phoneRegex.test(phone);
}

// Generate session token
export function generateSessionToken(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

// Session storage keys
export const STORAGE_KEYS = {
    USER: 'venntix_user',
    SESSION_TOKEN: 'venntix_session',
    REMEMBER_ME: 'venntix_remember',
} as const;

// Save session
export function saveSession(user: any, rememberMe: boolean = false) {
    if (typeof window === 'undefined') return;

    const token = generateSessionToken();

    if (rememberMe) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
        localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, token);
        localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
    } else {
        sessionStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
        sessionStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, token);
    }
}

// Get current session
export function getSession(): { user: any; token: string } | null {
    if (typeof window === 'undefined') return null;

    // Check localStorage first (remember me)
    let userStr = localStorage.getItem(STORAGE_KEYS.USER);
    let token = localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);

    // Fallback to sessionStorage
    if (!userStr) {
        userStr = sessionStorage.getItem(STORAGE_KEYS.USER);
        token = sessionStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);
    }

    if (userStr && token) {
        try {
            return { user: JSON.parse(userStr), token };
        } catch {
            return null;
        }
    }

    return null;
}

// Clear session (logout)
export function clearSession() {
    if (typeof window === 'undefined') return;

    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    sessionStorage.removeItem(STORAGE_KEYS.USER);
    sessionStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
}

// Delay helper for simulating API calls
export function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}
